import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, CircularProgress } from "@material-ui/core";
import { Room as RoomIcon } from "@material-ui/icons";

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
	iconUrl: icon,
	shadowUrl: iconShadow,
	iconSize: [25, 41],
	iconAnchor: [12, 41],
	popupAnchor: [1, -34],
	tooltipAnchor: [16, -28],
});

L.Marker.prototype.options.icon = DefaultIcon;

const useStyles = makeStyles((theme) => ({
	mapContainer: {
		height: 300,
		width: "100%",
		borderRadius: theme.shape.borderRadius,
		overflow: "hidden",
		border: "1px solid " + theme.palette.divider,
		marginTop: theme.spacing(1),
		position: "relative",
		zIndex: 0,
	},
	loadingOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		backgroundColor: "rgba(255,255,255,0.7)",
		zIndex: 1000,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	helpText: {
		fontSize: "0.75rem",
		color: theme.palette.text.secondary,
		marginTop: 4,
		display: "flex",
		alignItems: "center",
	}
}));

const MapEvents = ({ onLocationSelected }) => {
	useMapEvents({
		click(e) {
			onLocationSelected(e.latlng.lat, e.latlng.lng);
		},
	});
	return null;
};

const MapController = ({ position, zoomLevel }) => {
	const map = useMap();
	useEffect(() => {
		if (position) {
			map.flyTo(position, zoomLevel, { duration: 1.5 });
		}
	}, [position, zoomLevel, map]);
	return null;
};

const LocationSelector = ({ country, state, city, street, onLocationChange, initialLat, initialLng }) => {
	const classes = useStyles();
	const defaultCenter = [19.4326, -99.1332];
	
	const [position, setPosition] = useState(
		(initialLat && initialLng) ? [parseFloat(initialLat), parseFloat(initialLng)] : null
	);
	const [zoom, setZoom] = useState(4);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const delayDebounceFn = setTimeout(async () => {
			let queryParts = [];
			let currentZoom = 4;

			if (street) {
				queryParts.push(street);
				currentZoom = 16;
			}
			if (city) {
				queryParts.push(city);
				if (!street) currentZoom = 12;
			}
			if (state) {
				queryParts.push(state);
				if (!city && !street) currentZoom = 8;
			}
			if (country) {
				queryParts.push(country);
				if (!state && !city && !street) currentZoom = 5;
			}

			if (queryParts.length === 0) return;

			setLoading(true);
			const query = queryParts.join(", ");
			
			try {
				const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`);
				const data = await response.json();

				if (data && data.length > 0) {
					const lat = parseFloat(data[0].lat);
					const lon = parseFloat(data[0].lon);
					const newPos = [lat, lon];
					
					setPosition(newPos);
					setZoom(currentZoom);
					
					if (street && onLocationChange) {
						onLocationChange(lat, lon);
					}
				}
			} catch (error) {
				console.error("Error geocoding:", error);
			} finally {
				setLoading(false);
			}
		}, 1000);

		return () => clearTimeout(delayDebounceFn);
	}, [country, state, city, street]); 

	const handleMapClick = (lat, lng) => {
		setPosition([lat, lng]);
		setZoom(16);
		if (onLocationChange) onLocationChange(lat, lng);
	};

	return (
		<div>
			<Typography variant="subtitle2" gutterBottom>
				Ubicación exacta
			</Typography>
			<div className={classes.mapContainer}>
				{loading && (
					<div className={classes.loadingOverlay}>
						<CircularProgress />
					</div>
				)}
				<MapContainer 
					center={defaultCenter} 
					zoom={4} 
					style={{ height: "100%", width: "100%" }}
					scrollWheelZoom={true}
				>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					{position && street && <Marker position={position} />} 
					{position && <MapController position={position} zoomLevel={zoom} />}
					<MapEvents onLocationSelected={handleMapClick} />
				</MapContainer>
			</div>
			<div className={classes.helpText}>
				<RoomIcon style={{ fontSize: 16, marginRight: 4 }} />
				<span>El mapa se mueve automáticamente. Haz clic para fijar la ubicación exacta.</span>
			</div>
		</div>
	);
};

export default LocationSelector;